import type { CertCategory, Certification } from './certifications'

/** Un livello del catalogo servito da profile (`GET /v1/profile/certification-catalog`). */
export interface CatalogLevel {
  agencyCode: string
  code: string
  nameEn: string
  nameIt: string
  category: string
  minDives: number | null
  isoEquivalent: string | null
  minAge: number | null
  maxDepthM: number | null
  prerequisiteLevelCodes: string[]
}

const CATEGORY_MAP: Record<string, CertCategory> = {
  RECREATIONAL: 'Ricreativo',
  ADVANCED_RECREATIONAL: 'Ricreativo',
  RESCUE: 'Soccorso',
  LEADERSHIP: 'Professionale',
  TECHNICAL: 'Tecnico',
  CAVE: 'Overhead',
  WRECK: 'Overhead',
  REBREATHER: 'Rebreather',
  FREEDIVING: 'Apnea',
  SPECIALTY: 'Specialità',
}

/** "CMAS/FIPSAS" → "CMAS", "ASI SUB" → "ASI": la chiave con cui si abbinano le agenzie dell'elenco statico e quelle del catalogo. */
export function agencyKey(agency: string): string {
  return agency.trim().split(/[\s/]/)[0].toUpperCase()
}

const norm = (s: string) => s.trim().toLowerCase()

/**
 * Fonde il catalogo dell'API sull'elenco statico, che resta la base e il fallback:
 * - un livello già presente (stessa agenzia e stesso nome) prende dall'API età minima, profondità,
 *   prerequisiti ed equivalente ISO, quando l'API li ha; il resto (testi, note) resta quello statico;
 * - un livello che l'elenco statico non ha viene aggiunto.
 * Senza dati dall'API restituisce l'elenco statico invariato.
 */
export function mergeCatalog(base: Certification[], levels: CatalogLevel[] | null | undefined): Certification[] {
  if (!levels?.length) return base

  const result = base.map((c) => ({ ...c, prerequisitiIds: [...c.prerequisitiIds] }))
  const codeToId = new Map<string, string>() // "PSS:AOW" → id
  const matched = new Map<string, CatalogLevel>() // id → livello

  const find = (l: CatalogLevel) =>
    result.find((c) => agencyKey(c.agency) === l.agencyCode.toUpperCase() && (norm(c.brevetto) === norm(l.nameEn) || norm(c.brevetto) === norm(l.nameIt)))

  for (const l of levels) {
    const hit = find(l)
    if (hit) {
      codeToId.set(`${l.agencyCode}:${l.code}`, hit.id)
      matched.set(hit.id, l)
    }
  }

  const added: Certification[] = []
  for (const l of levels) {
    if (codeToId.has(`${l.agencyCode}:${l.code}`)) continue
    const sameAgency = result.find((c) => agencyKey(c.agency) === l.agencyCode.toUpperCase())
    const id = `${l.agencyCode.toLowerCase()}_${l.code.toLowerCase()}`
    codeToId.set(`${l.agencyCode}:${l.code}`, id)
    added.push({
      id,
      agency: sameAgency?.agency ?? l.agencyCode,
      categoria: CATEGORY_MAP[l.category] ?? 'Specialità',
      brevetto: l.nameEn,
      etaMinima: l.minAge,
      profonditaMaxM: l.maxDepthM,
      prerequisitiIds: [],
      abilitazione: '',
      note: '',
      isoEquivalent: l.isoEquivalent ?? undefined,
    })
    matched.set(id, l)
  }

  const all = [...result, ...added]
  for (const c of all) {
    const l = matched.get(c.id)
    if (!l) continue
    if (l.minAge != null) c.etaMinima = l.minAge
    if (l.maxDepthM != null) c.profonditaMaxM = l.maxDepthM
    if (!c.isoEquivalent && l.isoEquivalent) c.isoEquivalent = l.isoEquivalent
    // I prerequisiti dell'API sostituiscono quelli statici solo se si risolvono tutti: meglio un
    // prerequisito statico che un livello sbloccato per un codice che non conosciamo.
    const prereq = l.prerequisiteLevelCodes.map((code) => codeToId.get(`${l.agencyCode}:${code}`))
    if (prereq.length > 0 && prereq.every((p): p is string => !!p)) c.prerequisitiIds = prereq
  }
  return all
}
